// src/components/ModifierGroupEditor.js
// ─────────────────────────────────────────────────────────────
//  Reusable modifier-group / option form pieces. Pure forms — no API
//  calls, no opinions about how the caller persists `onSave(data)`.
//  Extracted from ModifierBuilder.js so the SAME editor UI is used both
//  for a simple MenuItem's modifiers and for a ModifierTemplate's groups.
// ─────────────────────────────────────────────────────────────
import React, { useState } from 'react';

export const DISPLAY_TYPES = [
  { id: 'radio',    label: 'Single select (Radio)',  icon: '🔘' },
  { id: 'checkbox', label: 'Multi select (Checkbox)', icon: '☑️' },
  { id: 'dropdown', label: 'Dropdown',               icon: '▼'  },
  { id: 'stepper',  label: 'Stepper +/−',            icon: '🔢' },
];

export const EMPTY_GROUP  = { name:'', nameAr:'', displayType:'radio', required:false, minSelect:0, maxSelect:1, sortOrder:0, dependsOnGroupId:'', dependsOnOptionId:'' };
export const EMPTY_OPTION = { name:'', nameAr:'', priceAdjustment:0, isDefault:false, maxQty:1, imageUrl:'' };

// ── Group form ──────────────────────────────────────────────────
export function GroupForm({ initial, groups, onSave, onCancel, loading }) {
  const [form, setForm] = useState({ ...initial });
  const f = (k) => ({ value: form[k] ?? '', onChange: (e) => setForm(p => ({ ...p, [k]: e.target.value })) });

  return (
    <div>
      <div className="grid2" style={{ gap:12, marginBottom:12 }}>
        <div>
          <div className="section-label" style={{ marginBottom:6 }}>Group Name (English) *</div>
          <input type="text" {...f('name')} placeholder="e.g. Size, Crust, Toppings" />
        </div>
        <div>
          <div className="section-label" style={{ marginBottom:6 }}>Group Name (Arabic)</div>
          <input type="text" {...f('nameAr')} placeholder="اختياري" dir="rtl" />
        </div>
        <div>
          <div className="section-label" style={{ marginBottom:6 }}>Display Type</div>
          <select value={form.displayType} onChange={e => setForm(p => ({ ...p, displayType: e.target.value }))}>
            {DISPLAY_TYPES.map(d => <option key={d.id} value={d.id}>{d.icon} {d.label}</option>)}
          </select>
        </div>
        <div>
          <div className="section-label" style={{ marginBottom:6 }}>Required?</div>
          <div style={{ display:'flex', alignItems:'center', gap:10, height:40 }}>
            <div className={`toggle${form.required ? ' on' : ''}`}
              onClick={() => setForm(p => ({ ...p, required: !p.required }))}
              style={{ cursor:'pointer' }} />
            <span style={{ fontSize:13, color:'var(--text2)' }}>{form.required ? 'Customer must choose' : 'Optional'}</span>
          </div>
        </div>
        <div>
          <div className="section-label" style={{ marginBottom:6 }}>Min Selections</div>
          <input type="number" value={form.minSelect} min="0"
            onChange={e => setForm(p => ({ ...p, minSelect: parseInt(e.target.value) || 0 }))} />
        </div>
        <div>
          <div className="section-label" style={{ marginBottom:6 }}>Max Selections (0 = unlimited)</div>
          <input type="number" value={form.maxSelect} min="0"
            onChange={e => setForm(p => ({ ...p, maxSelect: parseInt(e.target.value) || 1 }))} />
        </div>

        {/* Conditional display — only meaningful when `groups` (siblings) is provided */}
        {groups && (
          <div>
            <div className="section-label" style={{ marginBottom:6 }}>Show only when (parent group)</div>
            <select value={form.dependsOnGroupId || ''}
              onChange={e => setForm(p => ({ ...p, dependsOnGroupId: e.target.value || '', dependsOnOptionId: '' }))}>
              <option value="">Always show</option>
              {groups.filter(g => g.id !== initial?.id).map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>
        )}

        {groups && form.dependsOnGroupId && (
          <div>
            <div className="section-label" style={{ marginBottom:6 }}>Show only when option selected</div>
            <select value={form.dependsOnOptionId || ''}
              onChange={e => setForm(p => ({ ...p, dependsOnOptionId: e.target.value || '' }))}>
              <option value="">Any option in parent</option>
              {groups.find(g => g.id === form.dependsOnGroupId)?.options?.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div style={{ display:'flex', gap:8 }}>
        <button className="btn btn-primary" disabled={loading || !form.name} onClick={() => onSave(form)}>
          {loading ? 'Saving...' : 'Save Group'}
        </button>
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Option form ─────────────────────────────────────────────────
export function OptionForm({ initial, onSave, onCancel, loading }) {
  const [form, setForm] = useState({ ...initial });

  return (
    <div style={{ padding:12, background:'var(--bg4)', borderRadius:8, marginTop:8 }}>
      <div className="grid2" style={{ gap:10, marginBottom:10 }}>
        <div>
          <div className="section-label" style={{ marginBottom:4 }}>Option Name *</div>
          <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Large 12&quot;" />
        </div>
        <div>
          <div className="section-label" style={{ marginBottom:4 }}>Arabic Name</div>
          <input type="text" value={form.nameAr || ''} onChange={e => setForm(p => ({ ...p, nameAr: e.target.value }))} placeholder="اختياري" dir="rtl" />
        </div>
        <div>
          <div className="section-label" style={{ marginBottom:4 }}>Price Adjustment (AED)</div>
          <input type="number" value={form.priceAdjustment} step="0.5"
            onChange={e => setForm(p => ({ ...p, priceAdjustment: parseFloat(e.target.value) || 0 }))}
            placeholder="0 = free, 10 = +AED 10" />
        </div>
        <div>
          <div className="section-label" style={{ marginBottom:4 }}>Image URL</div>
          <input type="text" value={form.imageUrl || ''} onChange={e => setForm(p => ({ ...p, imageUrl: e.target.value }))} placeholder="https://..." />
        </div>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:10 }}>
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer' }}>
          <input type="checkbox" checked={form.isDefault}
            onChange={e => setForm(p => ({ ...p, isDefault: e.target.checked }))} />
          Pre-selected by default
        </label>
      </div>
      <div style={{ display:'flex', gap:8 }}>
        <button className="btn btn-primary btn-sm" disabled={loading || !form.name} onClick={() => onSave(form)}>
          {loading ? '...' : 'Save'}
        </button>
        <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
